import CloseIcon from "@mui/icons-material/Close";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { submitTemplateCard } from "@/cards/common/utils/submitTemplateCardHelper";
import { useDeletePage, useEditMainCard } from "@/cards/common/api/taascard.api";
import useThemeStore from "@/shared/stores/useThemeStore";

import {
  buildCreateTaasCardPages,
  getAddablePageTypesForTemplate,
  getCreateTaasCardTemplateFlags,
  getPageLockConfigForTemplate,
  insertNewTemplatePage,
  normalizePagesForBackend,
  removeTemplatePage,
  type AddablePageType,
} from "@/cards/common/utils/createTaasCardPages";
import {
  firstCreateTaasCardValidationMessage,
  getCreateTaasCardPageValidation,
  hasCreateTaasCardValidationErrors,
} from "@/cards/common/utils/createTaasCardValidation";
import { CreateTaasCardDetailsForm } from "../CreateTaasCardDetailsForm";
import { useCurrentTalentProfile } from "@/shared";
import { PublishedCard } from "../../types";
import { PageData } from "../../utils/useTemplatePageHelpers";

export interface EditTaasCardDialogProps {
  open: boolean;
  onClose: () => void;
  card: PublishedCard;
  onSaved?: () => void;
}

/** Edit dialog that mirrors step 3 of the create-card wizard. It re-uses
 *  `CreateTaasCardDetailsForm` so the add-page / remove-page / validation
 *  behavior matches the wizard exactly, and persists via PATCH instead of
 *  POST. */
export function EditTaasCardDialog({ open, onClose, card, onSaved }: EditTaasCardDialogProps) {
  const { t } = useTranslation();
  const { t: tValidation } = useTranslation("taascardValidation");
  const { darkMode } = useThemeStore();
  const { currentProfile } = useCurrentTalentProfile();
  const queryClient = useQueryClient();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [pages, setPages] = useState<PageData[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [pageToDeleteIndex, setPageToDeleteIndex] = useState<number | null>(null);
  const isUpdatingRef = useRef(false);

  // Re-seed editable pages from the card whenever the dialog opens.
  // `preservePageIds: true` carries each existing page's main-card id through
  // to the PATCH payload so the backend updates rows in place.
  useEffect(() => {
    if (open) {
      setPages(buildCreateTaasCardPages(card, t, { preservePageIds: true }));
      setIsSubmitting(false);
      setPageToDeleteIndex(null);
    }
  }, [open, card, t]);

  const c = useMemo(
    () => ({
      paper: darkMode ? "var(--neutral-900)" : "var(--neutralWhite)",
      border: darkMode ? "var(--neutral-800)" : "var(--primary-200)",
      title: darkMode ? "var(--neutralWhite)" : "var(--neutral-1000)",
      body: darkMode ? "var(--neutral-300)" : "var(--neutral-600)",
      primary: darkMode ? "var(--primary-400)" : "var(--primary-600)",
      close: darkMode ? "var(--neutral-300)" : "var(--primary-700)",
    }),
    [darkMode]
  );

  const { usesEndingImage } = getCreateTaasCardTemplateFlags(card, t);

  const pageValidationMessages = useMemo(
    () => getCreateTaasCardPageValidation(pages, card, tValidation),
    [pages, card, tValidation]
  );

  const hasValidationErrors = useMemo(
    () => hasCreateTaasCardValidationErrors(pageValidationMessages),
    [pageValidationMessages]
  );

  const addablePageTypes = useMemo(() => getAddablePageTypesForTemplate(card, t), [card, t]);

  const pageLockConfig = useMemo(() => getPageLockConfigForTemplate(card, t), [card, t]);

  const { update: editMainCard } = useEditMainCard({
    key: ["editMainCardFromThumbnail", currentProfile?.id, card.id],
    params: {
      id: Number(card.id),
      headers: {
        ...(currentProfile?.id && {
          "talent-id": currentProfile.id.toString(),
        }),
      },
    },
  });

  const pageToDelete = pageToDeleteIndex !== null ? pages[pageToDeleteIndex] : undefined;
  const pageToDeleteIdRaw = pageToDelete?.id;
  const pageToDeleteId =
    typeof pageToDeleteIdRaw === "number" && Number.isFinite(pageToDeleteIdRaw)
      ? pageToDeleteIdRaw
      : 0;

  const { update: deletePage, isLoading: isDeletingPage } = useDeletePage({
    key: ["deleteMainCardPage", pageToDeleteId],
    params: { id: pageToDeleteId },
  });

  const handleAddPage = useCallback((type: AddablePageType) => {
    setPages((prev) => insertNewTemplatePage(prev, type));
  }, []);

  // Pre-existing pages (those rehydrated from the API with an `id`) are
  // persisted on the backend, so removing them must hit DELETE
  // `/main-card/pages/:id`. Newly-added pages have no `id` yet and can be
  // dropped from local state quietly.
  const handleRemovePage = useCallback(
    (pageIndex: number) => {
      const page = pages[pageIndex];
      if (!page) return;
      const hasPersistedId = typeof page.id === "number" && Number.isFinite(page.id);
      if (hasPersistedId) {
        setPageToDeleteIndex(pageIndex);
        return;
      }
      setPages((prev) => removeTemplatePage(prev, pageIndex));
    },
    [pages]
  );

  const handleCancelDeletePage = useCallback(() => {
    if (isDeletingPage) return;
    setPageToDeleteIndex(null);
  }, [isDeletingPage]);

  const handleConfirmDeletePage = useCallback(async () => {
    if (pageToDeleteIndex === null || !pageToDeleteId) return;
    const indexToRemove = pageToDeleteIndex;
    try {
      await deletePage({});
      setPages((prev) => removeTemplatePage(prev, indexToRemove));
      queryClient.invalidateQueries({
        predicate: (q) =>
          Array.isArray(q.queryKey) &&
          typeof q.queryKey[0] === "string" &&
          (q.queryKey[0].startsWith("mainCards") || q.queryKey[0].startsWith("publishedCards")),
      });
      enqueueSnackbar(
        t("page_deleted_successfully", {
          ns: "taascard",
          defaultValue: "Page deleted successfully.",
        }),
        { variant: "success" }
      );
      setPageToDeleteIndex(null);
    } catch (err: any) {
      enqueueSnackbar(
        err?.response?.data?.message ||
          err?.message ||
          t("failed_to_delete_page", {
            ns: "taascard",
            defaultValue: "Failed to delete page.",
          }),
        { variant: "error" }
      );
    }
  }, [deletePage, pageToDeleteIndex, pageToDeleteId, queryClient, t]);

  const updatePageData = useCallback((pageIndex: number, newContent: any) => {
    if (isUpdatingRef.current) return;
    isUpdatingRef.current = true;

    setPages((prev) => {
      const updatedPages = [...prev];
      if (!updatedPages[pageIndex]) return prev;

      const currentContent = updatedPages[pageIndex]?.content;
      const isArrayContent = Array.isArray(newContent);
      const currentObjectContent =
        !Array.isArray(currentContent) && currentContent ? currentContent : {};

      const hasChanged = isArrayContent
        ? JSON.stringify(currentContent || []) !== JSON.stringify(newContent)
        : Object.keys(newContent).some((key) => {
            if (key === "images" || key === "url") {
              const currentValue = currentObjectContent[key];
              const updatedValue = newContent[key];
              // `url` is an array on Image/ImageGrid pages, but a plain
              // string (or File) on Instrument/Audio/Postcard/Youtube/Video
              // pages. Only use array comparison when at least one side is
              // actually an array � otherwise a string update was being
              // silently dropped because both sides were coerced to `[]`,
              // making the change look like a no-op.
              if (Array.isArray(currentValue) || Array.isArray(updatedValue)) {
                const current = Array.isArray(currentValue) ? currentValue : [];
                const updated = Array.isArray(updatedValue) ? updatedValue : [];
                return JSON.stringify(current) !== JSON.stringify(updated);
              }
              return currentValue !== updatedValue;
            }
            return currentObjectContent[key] !== newContent[key];
          });

      if (!hasChanged) {
        isUpdatingRef.current = false;
        return prev;
      }

      updatedPages[pageIndex] = {
        ...updatedPages[pageIndex],
        content: isArrayContent ? newContent : { ...currentObjectContent, ...newContent },
      };

      setTimeout(() => {
        isUpdatingRef.current = false;
      }, 100);

      return updatedPages;
    });
  }, []);

  const showValidationSnackbar = () => {
    enqueueSnackbar(
      firstCreateTaasCardValidationMessage(pageValidationMessages) ||
        t("please_complete_required_fields", {
          defaultValue: "Please complete all required fields.",
        }),
      { variant: "warning" }
    );
  };

  const handleSave = async () => {
    if (hasValidationErrors) {
      showValidationSnackbar();
      return;
    }

    try {
      setIsSubmitting(true);
      await submitTemplateCard({
        card,
        // Same normalization as the create wizard: rewrite FE-side
        // `Listening_Together_Card` to backend-canonical `Listen_Together_Card`
        // before PATCH so legacy cards (or any local state that still uses
        // the FE label) don't get rejected by the backend `type` enum.
        pagesData: normalizePagesForBackend(pages),
        usesEndingImage,
        createMainCard: editMainCard,
        t,
      });

      queryClient.invalidateQueries({
        predicate: (q) =>
          Array.isArray(q.queryKey) &&
          typeof q.queryKey[0] === "string" &&
          (q.queryKey[0].startsWith("mainCards") || q.queryKey[0].startsWith("publishedCards")),
      });

      enqueueSnackbar(
        t("card_updated_successfully", {
          ns: "taascard",
          defaultValue: "Card updated successfully.",
        }),
        { variant: "success" }
      );

      onSaved?.();
      onClose();
    } catch (error: any) {
      enqueueSnackbar(
        error?.response?.data?.message ||
          error?.message ||
          t("failed_to_update_card", {
            ns: "taascard",
            defaultValue: "Failed to update card.",
          }),
        { variant: "error" }
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const pageToDeleteLabel = pageToDelete
    ? `Page ${pageToDelete.order}`
    : t("this_page", { defaultValue: "this page" });

  return (
    <>
      <Dialog
        open={open}
        onClose={isSubmitting ? undefined : onClose}
        fullWidth
        fullScreen={isMobile}
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: { xs: 0, sm: 3 },
            bgcolor: c.paper,
            border: { xs: "none", sm: `1px solid ${c.border}` },
            backgroundImage: "none",
            maxHeight: { xs: "100%", sm: "90vh" },
            height: { xs: "100%", sm: "auto" },
            m: { xs: 0, sm: 4 },
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: { xs: 1.5, sm: 2.5 },
            pt: { xs: 1.5, sm: 2 },
            pb: 1,
            gap: 1,
            flexShrink: 0,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: { xs: "1rem", sm: "1.05rem" },
                color: c.title,
                lineHeight: 1.3,
              }}
              noWrap
            >
              {t("edit_card", { defaultValue: "Edit card" })}
            </Typography>
            <Typography
              sx={{
                fontSize: { xs: "0.7rem", sm: "0.8rem" },
                color: c.body,
                mt: 0.25,
              }}
              noWrap
            >
              {card.template || card.title}
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            disabled={isSubmitting}
            aria-label={t("close", { defaultValue: "Close" })}
            sx={{
              color: c.close,
              flexShrink: 0,
              "&:hover": {
                bgcolor: darkMode ? "var(--neutral-800)" : "var(--primary-100)",
              },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Box
          sx={{
            px: { xs: 1.5, sm: 2.5 },
            py: 1,
            overflowY: "auto",
            WebkitOverflowScrolling: "touch",
            flex: 1,
            minHeight: 0,
          }}
        >
          <CreateTaasCardDetailsForm
            card={card}
            pages={pages}
            isUpdatingRef={isUpdatingRef}
            updatePageData={updatePageData}
            pageValidationMessages={pageValidationMessages}
            colors={{ title: c.title, body: c.body }}
            darkMode={darkMode}
            addablePageTypes={addablePageTypes}
            onAddPage={handleAddPage}
            pageLockConfig={pageLockConfig}
            onRemovePage={handleRemovePage}
            isEditMode
          />
        </Box>

        <Box
          sx={{
            px: { xs: 1.5, sm: 2.5 },
            py: { xs: 1.25, sm: 2 },
            borderTop: `1px solid ${c.border}`,
            display: "flex",
            flexDirection: "column",
            gap: 1,
            flexShrink: 0,
            pb: { xs: "calc(env(safe-area-inset-bottom) + 12px)", sm: 2 },
          }}
        >
          {hasValidationErrors && (
            <Alert
              severity="error"
              sx={{
                py: 0,
                px: 1,
                fontSize: { xs: "0.7rem", sm: "0.75rem" },
                "& .MuiAlert-message": { py: 0.5 },
                "& .MuiAlert-icon": { py: 0.5 },
              }}
            >
              {t("please_complete_required_fields", {
                defaultValue: "Fix required fields",
              })}
            </Alert>
          )}

          <Stack
            direction="row"
            spacing={1}
            justifyContent="flex-end"
            alignItems="center"
            sx={{ width: "100%" }}
          >
            <Button
              onClick={onClose}
              disabled={isSubmitting}
              size={isMobile ? "small" : "medium"}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                color: c.body,
                px: { xs: 1, sm: 2 },
                display: { xs: "none", sm: "inline-flex" },
              }}
            >
              {t("cancel")}
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={isSubmitting || hasValidationErrors}
              size={isMobile ? "small" : "medium"}
              sx={{
                textTransform: "none",
                fontWeight: 700,
                borderRadius: 2,
                px: { xs: 2, sm: 2.5 },
                bgcolor: c.primary,
                "&:hover": {
                  bgcolor: darkMode ? "var(--primary-500)" : "var(--primary-700)",
                },
              }}
            >
              {isSubmitting ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                t("save_changes", { defaultValue: "Save changes" })
              )}
            </Button>
          </Stack>
        </Box>
      </Dialog>

      <Dialog
        open={pageToDeleteIndex !== null}
        onClose={handleCancelDeletePage}
        onClick={(e) => e.stopPropagation()}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            bgcolor: c.paper,
            borderRadius: 3,
            m: { xs: 2, sm: 3 },
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 700,
            color: c.title,
            fontFamily: '"Lexend", sans-serif',
          }}
        >
          {t("delete_page", {
            ns: "taascard",
            defaultValue: "Delete page",
          })}
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            sx={{
              color: c.body,
              fontSize: { xs: "0.875rem", sm: "0.95rem" },
              lineHeight: 1.55,
            }}
          >
            {t("delete_existing_page_confirm", {
              ns: "taascard",
              page: pageToDeleteLabel,
              defaultValue: `${pageToDeleteLabel} already exists on this card. Removing it will permanently delete the page and its content. This action cannot be undone.`,
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, pb: 2, gap: 0.5 }}>
          <Button
            onClick={handleCancelDeletePage}
            disabled={isDeletingPage}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              color: c.body,
            }}
          >
            {t("cancel")}
          </Button>
          <Button
            onClick={handleConfirmDeletePage}
            variant="contained"
            disabled={isDeletingPage}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              bgcolor: "#ef4444",
              color: "#fff",
              px: 2,
              "&:hover": { bgcolor: "#dc2626" },
              "&.Mui-disabled": { bgcolor: "#ef4444", opacity: 0.6 },
            }}
          >
            {isDeletingPage ? (
              <>
                <CircularProgress size={16} sx={{ mr: 1, color: "#fff" }} />
                {t("deleting", { defaultValue: "Deleting..." })}
              </>
            ) : (
              t("delete", { defaultValue: "Delete" })
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default EditTaasCardDialog;
